import { useState } from 'react';
import { useLoaderData, Form, useActionData, LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { apiClient } from '../../../lib/api-client';
import { requireUser } from '../../../lib/session.server';

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  
  try {
    const sessions = await apiClient.get('/account/sessions');
    return { user, sessions };
  } catch (error) {
    return { user, sessions: [] };
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get('intent');
  
  try {
    if (intent === 'change-password') {
      const currentPassword = formData.get('currentPassword');
      const newPassword = formData.get('newPassword');
      const confirmPassword = formData.get('confirmPassword');
      
      if (newPassword !== confirmPassword) {
        return { error: 'New passwords do not match' };
      }
      
      await apiClient.put('/account/password', {
        currentPassword,
        newPassword
      });
      
      return { success: true, message: 'Password updated successfully' };
    }
    
    if (intent === 'revoke-session') {
      const sessionId = formData.get('sessionId');
      await apiClient.delete(`/account/sessions/${sessionId}`);
      return { success: true, message: 'Session revoked successfully' };
    }
    
    if (intent === 'revoke-all-sessions') {
      await apiClient.delete('/account/sessions');
      return { success: true, message: 'All sessions revoked. Please log in again.' };
    }
    
    return { error: 'Invalid action' };
  } catch (error: any) {
    return { error: error.message || 'Operation failed' };
  }
}

export function SettingsSecurityPage() {
  const { sessions } = useLoaderData() as any;
  const actionData = useActionData() as any;
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Security Settings</h1>
        <p className="text-gray-600">Manage your account security and active sessions</p>
      </div>

      {actionData?.success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {actionData.message}
        </div>
      )}

      {actionData?.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {actionData.error}
        </div>
      )}

      <div className="space-y-6">
        {/* Change Password Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Change Password</h2>
          
          <Form method="post" className="space-y-4">
            <input type="hidden" name="intent" value="change-password" />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type={showPasswords ? "text" : "password"}
                name="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type={showPasswords ? "text" : "password"}
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters long
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type={showPasswords ? "text" : "password"}
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showPasswords"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="showPasswords" className="text-sm text-gray-700">
                Show passwords
              </label>
            </div>
            
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Update Password
            </button>
          </Form>
        </div>

        {/* Two-Factor Authentication Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Two-Factor Authentication</h2>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Add an extra layer of security to your account by enabling two-factor authentication.
            </p>
            
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Two-factor authentication is currently not enabled for your account.
              </p>
            </div>
            
            <button
              onClick={() => alert('2FA setup coming soon')}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Enable Two-Factor Authentication
            </button>
          </div>
        </div>

        {/* Active Sessions Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Active Sessions</h2>
            {sessions && sessions.length > 1 && (
              <Form method="post" className="inline">
                <input type="hidden" name="intent" value="revoke-all-sessions" />
                <button
                  type="submit"
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Revoke All Other Sessions
                </button>
              </Form>
            )}
          </div>
          
          {sessions && sessions.length > 0 ? (
            <div className="space-y-3">
              {sessions.map((session: any) => (
                <div key={session.id} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">
                        {session.device || 'Unknown Device'}
                        {session.current && (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            Current
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {session.ip_address || 'Unknown IP'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Last active: {new Date(session.last_active).toLocaleString()}
                      </p>
                    </div>
                    
                    {!session.current && (
                      <Form method="post" className="inline">
                        <input type="hidden" name="intent" value="revoke-session" />
                        <input type="hidden" name="sessionId" value={session.id} />
                        <button
                          type="submit"
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Revoke
                        </button>
                      </Form>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">No active sessions found.</p>
          )}
        </div>

        {/* Security Recommendations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Security Recommendations</h2>
          
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Use a strong, unique password that you don't use elsewhere</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Enable two-factor authentication for additional security</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Regularly review your active sessions and revoke any you don't recognize</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Keep your recovery email up to date</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Be cautious of phishing attempts - we'll never ask for your password via email</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}